import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardShortcuts} from '../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CalendarViewShiftDirectionsEnum} from '../../store/panels/calendars/calendarsPanelState';
import {CalendarsViewTypesEnum} from '../../store/panels/calendars/calendarsParameters';
import {TooltipLabelWithShortcut} from '../common/tooltips/tooltipLabelWithShortcut';

/*
 * Props.
 */

export interface LargeCalendarsControlsProps {
  viewType: CalendarsViewTypesEnum;
  focusedDate: number;
  shouldCalendarDisplayWeekends: boolean;
  onViewTypeSelect: HandlerOf<CalendarsViewTypesEnum>;
  onTodayButtonClick: Handler;
  onShouldCalendarDisplayWeekendsChange: HandlerOf<boolean>;
  onCalendarShiftClick: HandlerOf<CalendarViewShiftDirectionsEnum>;
  onNewEventClick: Handler;
  canCreateNewEvent: boolean;
  shouldShowRightControls: boolean;
}

/*
 * Styles.
 */

const StyledControlsDiv = styled.div`
  height: 62px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  padding: 8px;
  padding-bottom: 15px;
`;

const StyledLeftControlsDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  grid-column-gap: 9px;
  align-items: center;
`;

const StyledRightControlsDiv = styled.div`
  display: grid;
  grid-template-columns: auto;
  align-items: center;
`;

const StyledShiftButtonsDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 2px;
`;

const MonthTextDiv = styled.div`
  margin-left: 1px;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledViewTypeDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 8px;
  align-items: center;
  padding-left: 3px;
`;

const StyledContentDiv = styled.div`
  padding-left: 6.5px;
  padding-right: 6.5px;
`;

const StyledShowWeekendsDiv = styled.div`
  font-size: 14px;
  margin: 2px 8px 2px 2px;
`;

/*
 * Component.
 */

export const LargeCalendarsControls: FC<LargeCalendarsControlsProps> = (props) => {
  const maybeComposeKeyHandler = props.canCreateNewEvent
    ? {
        [KeyboardActionsEnum.COMPOSE]: props.onNewEventClick,
      }
    : {};

  const keyHandlers = {
    [KeyboardActionsEnum.CALENDAR_GO_TO_TODAY]: props.onTodayButtonClick,
    [KeyboardActionsEnum.CALENDAR_WEEK_VIEW]: () => props.onViewTypeSelect(CalendarsViewTypesEnum.WEEK),
    [KeyboardActionsEnum.CALENDAR_MONTH_VIEW]: () => props.onViewTypeSelect(CalendarsViewTypesEnum.MONTH),
    ...maybeComposeKeyHandler,
  };

  return (
    <StyledControlsDiv>
      <KeyboardShortcuts handlers={keyHandlers} />
      <StyledLeftControlsDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={props.onTodayButtonClick}
          renderTooltip={() => (
            <TooltipLabelWithShortcut
              label={<Trans id="calendar-controls-go-today">Go to Today</Trans>}
              keyboardAction={KeyboardActionsEnum.CALENDAR_GO_TO_TODAY}
            />
          )}
        >
          <StyledContentDiv>
            <Trans id="calendar-controls-today">Today</Trans>
          </StyledContentDiv>
        </Button>
        <StyledShiftButtonsDiv>
          <IconButton
            iconName="arrowBack"
            label={<Trans id="calendar-controls-previous">Previous</Trans>}
            onClick={() => props.onCalendarShiftClick(CalendarViewShiftDirectionsEnum.PREVIOUS)}
            keyboardAction={KeyboardActionsEnum.CALENDAR_PREVIOUS}
          />
          <IconButton
            iconName="arrowNext"
            label={<Trans id="calendar-controls-next">Next</Trans>}
            onClick={() => props.onCalendarShiftClick(CalendarViewShiftDirectionsEnum.NEXT)}
            keyboardAction={KeyboardActionsEnum.CALENDAR_NEXT}
          />
        </StyledShiftButtonsDiv>
        <MonthTextDiv>{DateTime.fromMillis(props.focusedDate).toFormat('LLLL yyyy')}</MonthTextDiv>
      </StyledLeftControlsDiv>
      {props.shouldShowRightControls && (
        <StyledRightControlsDiv>
          <PopoverCoordinator
            renderModal={(popoverProps) => renderDropdown(popoverProps, props)}
            renderButton={(isOpen) => renderButton(isOpen, props.viewType)}
          />
        </StyledRightControlsDiv>
      )}
    </StyledControlsDiv>
  );
};

/*
 * Button.
 */

function renderButton(_isOpen: boolean, viewType: CalendarsViewTypesEnum) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.MEDIUM}>
          <StyledViewTypeDiv>
            {renderViewTypeName(viewType)}
            <Icon name="chevronDownSmall" color={theme.greys.shade60} />
          </StyledViewTypeDiv>
        </Button>
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(popoverProps: PopoverTransferProps, props: LargeCalendarsControlsProps) {
  return (
    <SimpleDropdown {...popoverProps} footer={renderShowWeekendsFooter(props)}>
      <SimpleDropdownItem
        onSelect={() => props.onViewTypeSelect(CalendarsViewTypesEnum.WEEK)}
        isChecked={props.viewType === CalendarsViewTypesEnum.WEEK}
      >
        {renderViewTypeName(CalendarsViewTypesEnum.WEEK)}
      </SimpleDropdownItem>
      <SimpleDropdownItem
        onSelect={() => props.onViewTypeSelect(CalendarsViewTypesEnum.MONTH)}
        isChecked={props.viewType === CalendarsViewTypesEnum.MONTH}
      >
        {renderViewTypeName(CalendarsViewTypesEnum.MONTH)}
      </SimpleDropdownItem>
    </SimpleDropdown>
  );
}

function renderShowWeekendsFooter(props: LargeCalendarsControlsProps) {
  const {shouldCalendarDisplayWeekends, onShouldCalendarDisplayWeekendsChange} = props;

  return (
    <StyledShowWeekendsDiv>
      <CheckBox isChecked={shouldCalendarDisplayWeekends} onChange={onShouldCalendarDisplayWeekendsChange}>
        <Trans id="calendar-controls-show-weekends">Show weekends</Trans>
      </CheckBox>
    </StyledShowWeekendsDiv>
  );
}

function renderViewTypeName(viewType: CalendarsViewTypesEnum) {
  switch (viewType) {
    case CalendarsViewTypesEnum.MONTH:
      return <Trans id="calendar-controls-month">Month</Trans>;
    default:
      return <Trans id="calendar-controls-week">Week</Trans>;
  }
}
